/**
 * ArnLoadMore — Web Component handling infinite scroll via a "Load More" link.
 *
 * Fetches the next Shopify paginated page, extracts its product <li> nodes,
 * and appends them to the live product grid without a full page reload.
 *
 * The component is reset automatically whenever filters or sorting change,
 * because FacetFiltersForm.renderProductGridContainer() replaces the entire
 * #ProductGridContainer innerHTML, destroying and recreating this element.
 *
 * ── SEO note ──────────────────────────────────────────────────────────────
 * The "Load More" control is rendered as a real <a href="?page=N" rel="next">
 * so crawlers (Google et al) can follow the pagination chain server-side and
 * discover product links across pages. The click handler below preventDefault()s
 * the navigation so users get the JS-driven experience instead, while the
 * link target remains a valid, crawlable URL. After each load the href and
 * rel=next are updated so the chain continues correctly for crawlers that
 * may re-scan the DOM (and for users who right-click → "open in new tab").
 *
 * Required attributes:
 *   data-grid-id   → id of the <ul> that receives new product cards
 *   data-next-url  → URL of the next Shopify page; empty string on the last page
 *
 * Required child elements:
 *   .js-lm-btn             → the "Load More" <a> link
 *   .js-lm-counter         → the product count label (updated from fetched page)
 *   .arn-plp__loading-state → the loading spinner (toggled with tw-hidden)
 */
class ArnLoadMore extends HTMLElement {
  connectedCallback() {
    this._grid = document.getElementById(this.dataset.gridId);
    this._counter = this.querySelector('.js-lm-counter');
    this._btn = this.querySelector('.js-lm-btn');
    this._loading = false;

    // intercept the click so the browser does not navigate to the href.
    // The href stays in place purely for crawler discovery.
    this._btn?.addEventListener('click', (e) => {
      e.preventDefault();
      this._loadMore();
    });

    this._updateCounter();
  }

  // ─── Load more ────────────────────────────────────────────────────────────

  async _loadMore() {
    const nextUrl = this.dataset.nextUrl;
    if (!nextUrl || this._loading) return;

    this._setLoading(true);

    try {
      const html = await this._fetchPage(nextUrl);
      const meta = this._extractMeta(html);
      const items = this._extractItems(html);

      if (items.length) this._appendItems(items);

      // Update browser URL so it reflects the current pagination state
      // Extract the page param from the fetched URL and merge it into
      // the current location without reloading the page.
      const parsedUrl = new URL(nextUrl, window.location.origin);
      const nextPage = parsedUrl.searchParams.get('page');

      if (nextPage) {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('page', nextPage);

        // replaceState avoids adding extra entries to the browser history
        window.history.replaceState({}, '', currentUrl);
      }

      this.dataset.nextUrl = meta.nextUrl || '';
      if (!meta.nextUrl) {
        this._hideBtn();
      } else {
        // Keep the <a href> and rel=next in sync with the next page so
        // crawlers re-scanning the DOM (and "open in new tab" users) get
        // the correct target.
        this._updateBtnHref(meta.nextUrl);
      }

    } catch (err) {
      console.error('[arn-load-more] fetch error', err);
    } finally {
      this._setLoading(false);
    }
  }

  // ─── Fetch ────────────────────────────────────────────────────────────────

  async _fetchPage(url) {
    const res = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  }

  // ─── Parse ────────────────────────────────────────────────────────────────

  _extractItems(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const grid = doc.getElementById(this.dataset.gridId);
    // Grouping is handled server-side by arn-plp-product-card, so all <li>
    // nodes are appended as-is without client-side deduplication.
    return grid ? Array.from(grid.querySelectorAll('li')) : [];
  }

  _extractMeta(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const lmEl = doc.querySelector('arn-load-more');
    return {
      nextUrl: lmEl?.dataset.nextUrl || '',
    };
  }

  // ─── DOM ──────────────────────────────────────────────────────────────────

  _appendItems(items) {
    const frag = document.createDocumentFragment();
    items.forEach(li => frag.appendChild(document.adoptNode(li)));
    this._grid.appendChild(frag);

    this._updateCounter();
  }

  _setLoading(state) {
    this._loading = state;

    if (this._btn) {
      // For <a> there is no native disabled attribute, so use aria-disabled
      // and the disabled DOM property as a hint to assistive tech. Click
      // handler still preventDefault()s, so visually-disabling via
      // tw-hidden below is enough to block re-clicks.
      this._btn.setAttribute('aria-disabled', state ? 'true' : 'false');
    }

    // Toggle button visibility and spinner visibility.
    // The spinner element is rendered by the 'loading-spinner' snippet
    // with class 'arn-plp__loading-state tw-hidden' as its initial state.
    const spinner = this.querySelector('.arn-plp__loading-state');
    this._btn?.classList.toggle('tw-hidden', state);
    spinner?.classList.toggle('tw-hidden', !state);
  }

  /**
   * Update the Load More <a> link so its href + rel reflect the next page
   * to be loaded. Kept in sync with this.dataset.nextUrl which holds the
   * authoritative next-page URL across loads.
   */
  _updateBtnHref(nextUrl) {
    if (!this._btn) return;
    this._btn.setAttribute('href', nextUrl);
    this._btn.setAttribute('rel', 'next');
  }

  _hideBtn() {
    this._btn?.remove();
    this._btn = null;
  }

  // ─── Counter ──────────────────────────────────────────────────────────────────
  _updateCounter() {
    if (!this._counter || !this._grid) return;

    const count = Array.from(this._grid.querySelectorAll('li')).filter(li => li.querySelector('.arn-product-card')).length;
    const template = this._counter.dataset.counterTemplate;

    if (template) {
      this._counter.textContent = template.replace('__ARN_COUNT__', count);
    }
  }
}

customElements.define('arn-load-more', ArnLoadMore);